import { SidePanel } from '@brightdrop/side-panel';
import { Box } from '@mui/material';
import {
  lazy,
  Suspense,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { SIDEKICK_PANEL_WIDTH } from '../../constants';
import { useIsSidekickM1Enabled } from '../../flag';
import { selectIsConsentGranted } from '../../state/aiSidekickConsentSlice';
import {
  closeResetConfirm,
  openResetConfirm,
  selectIsResetConfirmLoading,
  selectIsResetConfirmOpen,
} from '../../state/aiSidekickSlice';
import type { StateWithSidekickChat } from '../../state/chatTypes';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import PanelHeader from '../PanelHeader';
import SidekickConsentGate from '../SidekickConsentGate';
import useStyles from './AISidekickPanel.styles';
import {
  SidekickPanelContext,
  type SidekickPanelContextValue,
} from './SidekickPanelContext';
import type { AISidekickPanelProps } from './types';

/**
 * Local, defensive read of the chat-slice message count for the
 * panel's "is the chat empty" gate. Cannot reuse the chat slice's
 * `selectMessages` selector because the panel may be mounted with
 * custom `children` that intentionally omit the chat slice (per the
 * `children` JSDoc below) — strict consumers like `MessageList`
 * still depend on `selectMessages` to fail loudly. Returns `0` when
 * the chat slice is absent so the no-op gate triggers and the
 * stranded-prompt path stays safe.
 */
const selectMessageCountForResetGate = (
  state: Partial<StateWithSidekickChat>
): number => state.aiSidekickChat?.messages.length ?? 0;

const LazyChatInterface = lazy(() => import('../ChatInterface'));

/**
 * Branded AI Sidekick drawer. Composes `@brightdrop/side-panel`'s
 * generic `SidePanel` with a branded header (title + close +
 * new-conversation buttons) and a fixed width.
 *
 * `open` and `onClose` are controlled by the consumer. The header's
 * new-conversation edit button TOGGLES the in-chat "Start a new
 * chat?" confirmation prompt: clicking it once opens the prompt,
 * clicking it again (or Cancel / X inside the prompt) dismisses it.
 * Clicking it on an empty conversation is a no-op — there's nothing
 * to reset, so the chrome refuses to commit a stranded prompt.
 *
 * The reset itself happens inside the prompt (dispatches `resetChat()`
 * and `resetUiState()`), with `onNewConversation` (if provided) firing
 * after the store has been reset. The optional callback is delivered
 * to the prompt via `SidekickPanelContext` to keep the seam free of
 * prop-drilling and to make room for future post-reset hooks.
 */
function AISidekickPanel({
  open,
  onClose,
  topOffsetPx = 0,
  overlayBelowPx,
  onNewConversation,
  onArtifactDownloaded,
  onNavigate,
  children,
  chipSet,
  isChipSetLoading,
}: AISidekickPanelProps) {
  const isM1Enabled = useIsSidekickM1Enabled();
  const { classes } = useStyles({ isM1Enabled });
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const dispatch = useDispatch();
  const isResetConfirmOpen = useSelector(selectIsResetConfirmOpen);
  const isResetConfirmLoading = useSelector(selectIsResetConfirmLoading);
  const messageCount = useSelector(selectMessageCountForResetGate);
  const isConsentGranted = useSelector(selectIsConsentGranted);
  // Fail closed: the chat is only reachable once status is `granted`.
  // Gating on "not granted" (rather than on the `denied` state) means a
  // missing consent reducer, an in-flight re-check, or an errored status
  // lookup keeps the gate mounted instead of silently exposing the chat.
  // Also gate on `open`: SidePanel keeps its children mounted (aria-hidden)
  // while closed, and the gate self-focuses on mount, so mounting it while
  // the drawer is closed would steal focus into an off-screen panel on page
  // load. It still fails closed once the drawer is open.
  const showConsentGate = open && !isConsentGranted;
  // A cached grant keeps the chat usable while the backend check runs.
  // If that check re-arms the gate, the modal covers the chat from then
  // on; a response already streaming finishes behind it.
  const isChatIsolated = showConsentGate;
  const chatSubtreeRef = useRef<HTMLDivElement>(null);

  // While the chat is isolated, `inert` drops it from the tab order and
  // the a11y tree (the 60% scrim only blocks the pointer, so without
  // this a keyboard user could Shift+Tab into the composer behind it).
  // `aria-hidden` is a fallback for environments without `inert` support
  // (there is no polyfill here) so screen readers still skip the
  // subtree. Set imperatively because React 18's DOM types do not
  // include the `inert` prop.
  useLayoutEffect(() => {
    const node = chatSubtreeRef.current;
    if (!node) {
      return;
    }
    if (isChatIsolated) {
      node.setAttribute('inert', '');
      node.setAttribute('aria-hidden', 'true');
    } else {
      node.removeAttribute('inert');
      node.removeAttribute('aria-hidden');
    }
  }, [isChatIsolated]);

  const handleNewConversation = useCallback(() => {
    if (isResetConfirmLoading) {
      // Reset is in flight — refuse to dismiss the prompt because
      // unmounting it cancels the pending timer and silently aborts
      // the reset. Mirrors the prompt's own Cancel/X disabled gate.
      return;
    }
    if (isResetConfirmOpen) {
      dispatch(closeResetConfirm());
      return;
    }
    if (messageCount === 0) {
      // Nothing to reset — refuse to commit a stranded prompt.
      // Also covers the panel-without-chat-slice case (custom children).
      return;
    }
    dispatch(openResetConfirm());
  }, [dispatch, isResetConfirmLoading, isResetConfirmOpen, messageCount]);

  const handleClose = useCallback(() => {
    if (isResetConfirmLoading) {
      // Reset is in flight — refuse to close the panel because
      // unmounting `ResetConversationPrompt` fires its cleanup
      // `clearTimeout` and silently aborts the in-flight reset.
      // Wraps every dismiss path the package owns (header X, scrim
      // click, scrim Escape) so the gate cannot be bypassed.
      return;
    }
    onClose();
  }, [isResetConfirmLoading, onClose]);

  const contextValue = useMemo<SidekickPanelContextValue>(
    () => ({ onNewConversation, onArtifactDownloaded, onNavigate }),
    [onArtifactDownloaded, onNavigate, onNewConversation]
  );

  const title = t('panel.title');

  return (
    <SidekickPanelContext.Provider value={contextValue}>
      <SidePanel
        className={classes.panelSurface}
        open={open}
        onClose={handleClose}
        width={SIDEKICK_PANEL_WIDTH}
        topOffsetPx={topOffsetPx}
        overlayBelowPx={overlayBelowPx}
        ariaLabel={title}
        header={
          <PanelHeader
            title={title}
            dismissAriaLabel={t(
              isM1Enabled ? 'dock.minimizeAriaLabel' : 'panel.closeAriaLabel'
            )}
            newConversationAriaLabel={t('panel.newConversationAriaLabel')}
            onClose={handleClose}
            onNewConversation={handleNewConversation}
            isNewConversationActive={isResetConfirmOpen}
            isNewConversationDisabled={isResetConfirmLoading}
            isM1Enabled={isM1Enabled}
          />
        }
      >
        <Box sx={{ position: 'relative', height: '100%' }}>
          <Box
            ref={chatSubtreeRef}
            sx={{ height: '100%' }}
            data-testid="ai-sidekick-panel-body"
          >
            {children ?? (
              <Suspense fallback={null}>
                <LazyChatInterface
                  chipSet={chipSet}
                  chipSetIsLoading={isChipSetLoading}
                  isM1Enabled={isM1Enabled}
                  isInputFocusEnabled={open && isConsentGranted}
                />
              </Suspense>
            )}
          </Box>
          {showConsentGate ? <SidekickConsentGate /> : null}
        </Box>
      </SidePanel>
    </SidekickPanelContext.Provider>
  );
}

export default AISidekickPanel;
